import React, {useEffect, useRef, useState} from "react";
import axios, {baseUrl, wsBaseUrl} from "../telemedicineUniv";
import {useParams} from "react-router-dom";
import "./TelemedicineRoom.css";
const SparklesIcon = ({size = 18}) => (<svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
        display: "inline-block", verticalAlign: "middle", flexShrink: 0
    }}
>
    <path
        d="M12 3L13.4 8.6L19 10L13.4 11.4L12 17L10.6 11.4L5 10L10.6 8.6L12 3Z"
        fill="currentColor"
    />
    <path
        d="M19 15L19.7 17.3L22 18L19.7 18.7L19 21L18.3 18.7L16 18L18.3 17.3L19 15Z"
        fill="currentColor"
    />
    <path
        d="M5 15L5.5 16.5L7 17L5.5 17.5L5 19L4.5 17.5L3 17L4.5 16.5L5 15Z"
        fill="currentColor"
    />
</svg>);
function TelemedicineRoom() {
    const {id} = useParams();
    // =========================================================
    // REFS
    // =========================================================
    const socketRef = useRef(null);
    const pcRef = useRef(null);
    const localVideo = useRef(null);
    const remoteVideo = useRef(null);
    const localStreamRef = useRef(null);
    const myChannelRef = useRef(null);
    const remoteChannelRef = useRef(null);
    const mountedRef = useRef(false);
    const initializingRef = useRef(false);
    // =========================================================
    // STATE
    // =========================================================
    const [appointment, setAppointment] = useState(null);
    const [session, setSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sessionLoading, setSessionLoading] = useState(true);
    const [cameraStarted, setCameraStarted] = useState(false);
    const [socketConnected, setSocketConnected] = useState(false);
    const [peerConnected, setPeerConnected] = useState(false);
    const [callStarted, setCallStarted] = useState(false);
    const [error, setError] = useState("");
    const [sessionJoined, setSessionJoined] = useState(false);
    const [sessionStarted, setSessionStarted] = useState(false);
    const [endingCall, setEndingCall] = useState(false);
    const sessionIdRef = useRef(null);
    const pendingIceCandidatesRef = useRef([]);
    // =========================================================
    // USER INFORMATION
    // =========================================================
    // =========================================================
// AUTHENTICATION
// =========================================================
    const patientLoggedIn = localStorage.getItem("pat_login") === "true" && !!localStorage.getItem("patAuthTokens");
    const mainLoggedIn = localStorage.getItem("user_login") === "true" && !!localStorage.getItem("authTokens");
// =========================================================
// USER TYPE
// =========================================================
    const userType = patientLoggedIn ? "patient" : "doctor";
    const telemedicineRole = localStorage.getItem("telemedicine_role") || "patient";
    const canViewClinicalSection = ["doctor", "admin"].includes(telemedicineRole.toLowerCase());
// =========================================================
// USER ID
// =========================================================
    const userId = patientLoggedIn ? localStorage.getItem("pat_id") : localStorage.getItem("user_id");
// =========================================================
// HOSPITAL
// =========================================================
    const hospitalCode = localStorage.getItem("hospital_code") || "";
// =========================================================
// LOCATION
// =========================================================
    const locationId = localStorage.getItem("location") || localStorage.getItem("userLocation") || "1";
    // =========================================================
// VITALS
// =========================================================
    const [vitals, setVitals] = useState({
        systolic: "", diastolic: "", pulse: "", spo2: "", temperature: "", respiratory_rate: "", blood_glucose: ""
    });
    const [vitalsLoading, setVitalsLoading] = useState(false);
    const [vitalsSaving, setVitalsSaving] = useState(false);
    const [vitalsMessage, setVitalsMessage] = useState("");
    const [vitalsError, setVitalsError] = useState("");
    const [microphoneStarted, setMicrophoneStarted] = useState(true);
    // =========================================================
// CHAT
// =========================================================
    const [chatOpen, setChatOpen] = useState(false);
    const [chatMessage, setChatMessage] = useState("");
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [screenSharing, setScreenSharing] = useState(false);
    const screenStreamRef = useRef(null);
    const screenTrackRef = useRef(null);
// =========================================================
// CLINICAL NOTES
// =========================================================
    const [clinicalNotes, setClinicalNotes] = useState("");
    const [notesSaving, setNotesSaving] = useState(false);
    const [notesMessage, setNotesMessage] = useState("");
    const [notesError, setNotesError] = useState("");
    // =========================================================
// AI CLINICAL COPILOT
// =========================================================
    const [aiLoading, setAiLoading] = useState(false);
    const [aiContextLoading, setAiContextLoading] = useState(false);
    const [aiContext, setAiContext] = useState(null);
    const [aiAnalysis, setAiAnalysis] = useState(null);
    const [aiSummary, setAiSummary] = useState(null);
    const [aiClinicalNotes, setAiClinicalNotes] = useState([]);
    const [aiError, setAiError] = useState("");
    const [aiMessage, setAiMessage] = useState("");
    const [activeAISection, setActiveAISection] = useState("analysis");
    // =========================================================
    // INITIALIZATION
    // =========================================================
    useEffect(() => {
        mountedRef.current = true;
        if (!initializingRef.current) {
            initializingRef.current = true;
            initializeTelemedicine();
        }
        return () => {
            mountedRef.current = false;
            cleanup();
        };
    }, []);
    // =========================================================
    // INITIALIZE TELEMEDICINE
    // =========================================================
    const initializeTelemedicine = async () => {
        try {
            if (!mountedRef.current) {
                return;
            }
            setLoading(true);
            setError("");
            // =====================================================
            // 1. LOAD APPOINTMENT
            // =====================================================
            const appointmentData = await loadAppointment();
            if (!mountedRef.current || !appointmentData || !appointmentData.id) {
                return;
            }
            console.log("INITIALIZATION APPOINTMENT ID:", appointmentData.id);
            // =====================================================
            // 2. CREATE / GET SESSION
            // =====================================================
            const sessionData = await createOrGetSession(appointmentData.id);
            if (!mountedRef.current || !sessionData) {
                return;
            }
            // =====================================================
            // 3. LOAD VITALS
            // =====================================================
            await loadVitals(sessionData.id);
            // =====================================================
            // 4. LOAD AI DATA
            // =====================================================
            await Promise.all([loadAIContext(sessionData.id), loadAIAnalysis(sessionData.id), loadAISummary(sessionData.id), loadAIClinicalNotes(sessionData.id)]);
            if (!mountedRef.current) {
                return;
            }
            // =====================================================
            // 5. JOIN SESSION
            // =====================================================
            const joined = await joinSession(sessionData.id);
            if (!mountedRef.current || !joined) {
                return;
            }
            // =====================================================
            // 6. CAMERA / MICROPHONE
            // =====================================================
            try {
                await startCamera();
            } catch (cameraError) {
                console.error("Camera / microphone initialization failed:", cameraError);
                if (mountedRef.current) {
                    setError("Camera or microphone permission is not available. " + "Please allow camera and microphone access.");
                }
            }
            if (!mountedRef.current) {
                return;
            }
            // =====================================================
            // 7. WEBSOCKET
            // =====================================================
            connectSocket(sessionData.room_id);
        } catch (error) {
            console.error("Telemedicine initialization error:", error);
            if (mountedRef.current) {
                setError(error?.message || "Unable to initialize telemedicine room.");
            }
        } finally {
            if (mountedRef.current) {
                setLoading(false);
                setSessionLoading(false);
            }
        }
    };
    useEffect(() => {
        if (localVideo.current && localStreamRef.current) {
            console.log("Attaching local stream to video element");
            localVideo.current.srcObject = localStreamRef.current;
            localVideo.current
                .play()
                .catch(error => {
                    console.warn("Local video autoplay:", error);
                });
        }
    }, [loading, sessionLoading, cameraStarted]);
    // =========================================================
// SAVE VITALS
// =========================================================
    const saveVitals = async () => {
        if (!session?.id) {
            setVitalsError("Telemedicine session is not available.");
            return;
        }
        try {
            setVitalsSaving(true);
            setVitalsMessage("");
            setVitalsError("");
            const payload = {
                session: session.id,
                patient: appointment?.patient?.id || null,
                appointment: appointment?.id || session?.appointment || null,
                systolic: vitals.systolic === "" ? null : Number(vitals.systolic),
                diastolic: vitals.diastolic === "" ? null : Number(vitals.diastolic),
                pulse: vitals.pulse === "" ? null : Number(vitals.pulse),
                spo2: vitals.spo2 === "" ? null : Number(vitals.spo2),
                temperature: vitals.temperature === "" ? null : Number(vitals.temperature),
                respiratory_rate: vitals.respiratory_rate === "" ? null : Number(vitals.respiratory_rate),
                blood_glucose: vitals.blood_glucose === "" ? null : Number(vitals.blood_glucose)
            };
            console.log("Saving telemedicine vitals:", payload);
            const response = await axios.post(`${baseUrl}telemedicine/vitals/`, payload);
            console.log("Vitals saved:", response.data);
            setVitalsMessage("Vitals saved successfully.");
            /*
             * If backend returns the saved object,
             * update the form from it.
             */
            const saved = response.data?.vital || response.data?.data || response.data;
            if (saved && typeof saved === "object") {
                setVitals(previous => ({
                    ...previous,
                    systolic: saved.systolic ?? previous.systolic,
                    diastolic: saved.diastolic ?? previous.diastolic,
                    pulse: saved.pulse ?? previous.pulse,
                    spo2: saved.spo2 ?? previous.spo2,
                    temperature: saved.temperature ?? previous.temperature,
                    respiratory_rate: saved.respiratory_rate ?? previous.respiratory_rate,
                    blood_glucose: saved.blood_glucose ?? previous.blood_glucose
                }));
            }
        } catch (error) {
            console.error("Save vitals error:", error);
            console.error("Backend response:", error?.response?.data);
            setVitalsError(error?.response?.data?.message || error?.response?.data?.detail || "Unable to save patient vitals.");
        } finally {
            setVitalsSaving(false);
        }
    };
    // =========================================================
    // LOAD APPOINTMENT
    // =========================================================
    const loadAppointment = async () => {
        try {
            console.log("======================================");
            console.log("LOAD TELEMEDICINE APPOINTMENT");
            console.log("URL ID:", id);
            console.log("TELEMEDICINE ROLE:", localStorage.getItem("telemedicine_role"));
            console.log("======================================");
            if (!id) {
                throw new Error("Appointment ID is missing from URL.");
            }
            const response = await axios.get(`${baseUrl}telemedicine/appointment/${id}/`);
            console.log("APPOINTMENT RESPONSE:", response.data);
            /*
             * Backend response:
             *
             * {
             *     success: true,
             *     appointment: {...}
             * }
             */
            const appointment = response.data?.appointment || response.data;
            if (!appointment?.id) {
                throw new Error("Appointment information is invalid.");
            }
            console.log("ACTUAL APPOINTMENT:", appointment);
            setAppointment(appointment);
            return appointment;
        } catch (error) {
            console.error("LOAD APPOINTMENT ERROR:", error);
            console.error("BACKEND RESPONSE:", error?.response?.data);
            setError(error?.response?.data?.message || error?.response?.data?.detail || "Unable to load telemedicine appointment.");
            return null;
        }
    };
    // =========================================================
    // CREATE / GET TELEMEDICINE SESSION
    // =========================================================
    const createOrGetSession = async (appointmentId) => {
        try {
            console.log("======================================");
            console.log("CREATE / GET TELEMEDICINE SESSION");
            console.log("APPOINTMENT ID:", appointmentId);
            console.log("======================================");
            if (!appointmentId) {
                console.error("Cannot create telemedicine session: appointment ID missing");
                setError("Appointment information is missing.");
                return null;
            }
            const response = await axios.post(`${baseUrl}telemedicine/sessions/`, {
                appointment: appointmentId
            });
            console.log("SESSION RESPONSE:", response.data);
            if (!response.data?.success) {
                throw new Error(response.data?.message || "Unable to create telemedicine session.");
            }
            const session = response.data.session;
            if (!session) {
                throw new Error("Telemedicine session was not returned by server.");
            }
            setSession(session);
            sessionIdRef.current = session.id;
            setSessionStarted(session.status === "ACTIVE");
            return session;
        } catch (error) {
            console.error("CREATE SESSION ERROR:", error);
            console.error("BACKEND RESPONSE:", error?.response?.data);
            setError(error?.response?.data?.message || error?.response?.data?.detail || error.message || "Unable to create telemedicine session.");
            return null;
        }
    };
    // =========================================================
// JOIN TELEMEDICINE SESSION
// =========================================================
    const joinSession = async (sessionId) => {
        try {
            console.log("Joining telemedicine session:", sessionId);
            console.log("Participant role:", userType);
            const response = await axios.post(`${baseUrl}telemedicine/sessions/${sessionId}/join/?location=${locationId}`, {
                role: userType
            });
            console.log("Join session response:", response.data);
            setSessionJoined(true);
            return response.data;
        } catch (error) {
            console.error("Join telemedicine session error:", error);
            setSessionJoined(false);
            setError(error?.response?.data?.detail || error?.response?.data?.message || "Unable to join telemedicine session.");
            return null;
        }
    };
// =========================================================
// START TELEMEDICINE SESSION
// =========================================================
    const startSession = async () => {
        if (!session?.id) {
            setError("Telemedicine session is not available.");
            return false;
        }
        try {
            console.log("Starting telemedicine session:", session.id);
            const response = await axios.post(`${baseUrl}telemedicine/sessions/${session.id}/start/`, {});
            console.log("Start session response:", response.data);
            setSessionStarted(true);
            // -------------------------------------------------
            // Update local session state
            // -------------------------------------------------
            setSession(previous => {
                if (!previous) {
                    return previous;
                }
                return {
                    ...previous,
                    status: response.data?.session?.status || "ACTIVE",
                    started_at: response.data?.session?.started_at || previous.started_at
                };
            });
            return true;
        } catch (error) {
            console.error("Start telemedicine session error:", error);
            setError(error?.response?.data?.detail || error?.response?.data?.message || "Unable to start telemedicine session.");
            return false;
        }
    };
// =========================================================
// END TELEMEDICINE SESSION
// =========================================================
    const endSession = async () => {
        if (!session?.id) {
            return;
        }
        try {
            setEndingCall(true);
            console.log("Ending telemedicine session:", session.id);
            const response = await axios.post(`${baseUrl}telemedicine/sessions/${session.id}/end/`, {});
            console.log("End session response:", response.data);
            setSessionStarted(false);
            setCallStarted(false);
            setSession(previous => {
                if (!previous) {
                    return previous;
                }
                return {
                    ...previous,
                    status: response.data?.session?.status || "ENDED",
                    ended_at: response.data?.session?.ended_at || previous.ended_at,
                    duration_seconds: response.data?.session?.duration_seconds ?? previous.duration_seconds
                };
            });
            // -------------------------------------------------
            // Close WebRTC
            // -------------------------------------------------
            if (pcRef.current) {
                pcRef.current.close();
                pcRef.current = null;
            }
            // -------------------------------------------------
            // Stop media
            // -------------------------------------------------
            if (localStreamRef.current) {
                localStreamRef.current
                    .getTracks()
                    .forEach(track => {
                        track.stop();
                    });
                localStreamRef.current = null;
            }
            if (localVideo.current) {
                localVideo.current.srcObject = null;
            }
            if (remoteVideo.current) {
                remoteVideo.current.srcObject = null;
            }
            setCameraStarted(false);
        } catch (error) {
            console.error("End telemedicine session error:", error);
            setError(error?.response?.data?.detail || error?.response?.data?.message || "Unable to end telemedicine session.");
        } finally {
            setEndingCall(false);
        }
    };
// =========================================================
// LEAVE TELEMEDICINE SESSION
// =========================================================
    const leaveSession = async () => {
        if (!session?.id) {
            return;
        }
        try {
            console.log("Leaving telemedicine session:", session.id);
            const response = await axios.post(`${baseUrl}telemedicine/sessions/${session.id}/leave/`, {});
            console.log("Leave session response:", response.data);
            setSessionJoined(false);
        } catch (error) {
            console.error("Leave telemedicine session error:", error);
        }
    };
    // =========================================================
// VITALS CHANGE
// =========================================================
    const handleVitalsChange = (event) => {
        const {name, value} = event.target;
        setVitals(previous => ({
            ...previous, [name]: value
        }));
        setVitalsMessage("");
        setVitalsError("");
    };
// =========================================================
// LOAD VITALS
// =========================================================
    const loadVitals = async (sessionId) => {
        if (!sessionId) {
            return;
        }
        try {
            setVitalsLoading(true);
            setVitalsError("");
            console.log("Loading vitals for session:", sessionId);
            const response = await axios.get(`${baseUrl}telemedicine/vitals/?session=${sessionId}`);
            console.log("Vitals response:", response.data);
            let data = response.data;
            /*
             * Depending on the serializer response,
             * support both:
             *
             * {
             *    results: [...]
             * }
             *
             * and
             *
             * [...]
             */
            if (data?.results) {
                data = data.results;
            }
            if (Array.isArray(data)) {
                if (data.length === 0) {
                    return;
                }
                data = data[0];
            }
            if (!data) {
                return;
            }
            setVitals({
                systolic: data.systolic ?? data.blood_pressure_systolic ?? "",
                diastolic: data.diastolic ?? data.blood_pressure_diastolic ?? "",
                pulse: data.pulse ?? data.heart_rate ?? "",
                spo2: data.spo2 ?? data.oxygen_saturation ?? "",
                temperature: data.temperature ?? "",
                respiratory_rate: data.respiratory_rate ?? "",
                blood_glucose: data.blood_glucose ?? ""
            });
        } catch (error) {
            console.error("Load vitals error:", error);
            /*
             * Do not show an error when there simply
             * aren't any vitals yet.
             */
            if (error?.response?.status !== 404) {
                setVitalsError(error?.response?.data?.message || "Unable to load patient vitals.");
            }
        } finally {
            setVitalsLoading(false);
        }
    };
    // =========================================================
// LOAD AI CONTEXT
// =========================================================
    const loadAIContext = async (sessionId) => {
        if (!sessionId) {
            return;
        }
        try {
            setAiContextLoading(true);
            setAiError("");
            console.log("Loading AI context for session:", sessionId);
            const response = await axios.get(`${baseUrl}telemedicine/sessions/${sessionId}/ai-context/`);
            console.log("AI context response:", response.data);
            setAiContext(response.data?.context || response.data || null);
        } catch (error) {
            console.error("AI context error:", error);
            setAiError(error?.response?.data?.message || error?.response?.data?.detail || "Unable to load AI clinical context.");
        } finally {
            setAiContextLoading(false);
        }
    };
// =========================================================
// LOAD AI ANALYSIS
// =========================================================
    const loadAIAnalysis = async (sessionId) => {
        if (!sessionId) {
            return;
        }
        try {
            const response = await axios.get(`${baseUrl}telemedicine/sessions/${sessionId}/ai-analysis/`);
            console.log("AI analysis:", response.data);
            let data = response.data;
            if (data?.results) {
                data = data.results;
            }
            if (Array.isArray(data)) {
                data = data.length > 0 ? data[0] : null;
            }
            setAiAnalysis(data);
        } catch (error) {
            if (error?.response?.status !== 404) {
                console.error("AI analysis loading error:", error);
            }
        }
    };
// =========================================================
// LOAD AI SUMMARY
// =========================================================
    const loadAISummary = async (sessionId) => {
        if (!sessionId) {
            return;
        }
        try {
            const response = await axios.get(`${baseUrl}telemedicine/sessions/${sessionId}/ai-summary/`);
            console.log("AI summary:", response.data);
            let data = response.data;
            if (data?.results) {
                data = data.results;
            }
            if (Array.isArray(data)) {
                data = data.length > 0 ? data[0] : null;
            }
            setAiSummary(data);
        } catch (error) {
            if (error?.response?.status !== 404) {
                console.error("AI summary loading error:", error);
            }
        }
    };
// =========================================================
// LOAD AI CLINICAL NOTES
// =========================================================
    const loadAIClinicalNotes = async (sessionId) => {
        if (!sessionId) {
            return;
        }
        try {
            const response = await axios.get(`${baseUrl}telemedicine/sessions/${sessionId}/clinical-notes/`);
            console.log("AI clinical notes:", response.data);
            let data = response.data;
            if (data?.results) {
                data = data.results;
            }
            if (!Array.isArray(data)) {
                data = data ? [data] : [];
            }
            setAiClinicalNotes(data);
        } catch (error) {
            if (error?.response?.status !== 404) {
                console.error("AI clinical notes error:", error);
            }
        }
    };
// =========================================================
// RUN TELEMEDICINE AI
// =========================================================
    const runTelemedicineAI = async () => {
        if (!session?.id) {
            setAiError("Telemedicine session is not available.");
            return;
        }
        try {
            setAiLoading(true);
            setAiError("");
            setAiMessage("");
            console.log("Running Telemedicine AI:", session.id);
            const response = await axios.post(`${baseUrl}telemedicine/sessions/${session.id}/ai-run/`, {});
            console.log("AI execution response:", response.data);
            setAiMessage(response.data?.message || "AI clinical analysis completed.");
            // -------------------------------------------------
            // Reload AI results
            // -------------------------------------------------
            await Promise.all([loadAIContext(session.id), loadAIAnalysis(session.id), loadAISummary(session.id), loadAIClinicalNotes(session.id)]);
            // -------------------------------------------------
            // Show analysis
            // -------------------------------------------------
            setActiveAISection("analysis");
        } catch (error) {
            console.error("Telemedicine AI execution error:", error);
            console.error("Backend:", error?.response?.data);
            setAiError(error?.response?.data?.message || error?.response?.data?.detail || "Unable to run AI clinical analysis.");
        } finally {
            setAiLoading(false);
        }
    };
    // =========================================================
    // CAMERA + MICROPHONE
    // =========================================================
    const startCamera = async () => {
        try {
            // =====================================================
            // ALREADY RUNNING
            // =====================================================
            if (localStreamRef.current) {
                const activeTracks = localStreamRef.current
                    .getTracks()
                    .filter(track => track.readyState === "live");
                if (activeTracks.length > 0) {
                    console.log("Camera already running.");
                    if (localVideo.current) {
                        localVideo.current.srcObject = localStreamRef.current;
                    }
                    setCameraStarted(true);
                    return localStreamRef.current;
                }
            }
            // =====================================================
            // BROWSER SUPPORT
            // =====================================================
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error("Browser does not support camera access.");
            }
            // =====================================================
            // GET MEDIA
            // =====================================================
            const stream = await navigator.mediaDevices.getUserMedia({
                video: true, audio: true
            });
            if (localVideo.current) {
                localVideo.current.srcObject = stream;
            }
            setCameraStarted(true);
            // Component may have been unmounted
            // while permission dialog was open.
            if (!mountedRef.current) {
                stream.getTracks().forEach(track => {
                    track.stop();
                });
                return null;
            }
            localStreamRef.current = stream;
            // =====================================================
            // VIDEO
            // =====================================================
            if (localVideo.current) {
                localVideo.current.srcObject = stream;
            }
            setCameraStarted(true);
            console.log("Camera and microphone started.");
            return stream;
        } catch (error) {
            console.error("Camera / microphone error:", error);
            setCameraStarted(false);
            throw error;
        }
    };
    const getTelemedicineAccessToken = () => {
        const storageKey = patientLoggedIn ? "patAuthTokens" : "authTokens";
        const rawTokens = localStorage.getItem(storageKey);
        if (!rawTokens) {
            return null;
        }
        try {
            const tokens = JSON.parse(rawTokens);
            return (tokens?.access || tokens?.access_token || tokens?.token || null);
        } catch (error) {
            console.error("Unable to parse authentication tokens:", error);
            return null;
        }
    };
    // =========================================================
// SEND CHAT MESSAGE
// =========================================================
    const sendChatMessage = () => {
        const text = chatMessage.trim();
        if (!text) {
            return;
        }
        if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
            setError("Telemedicine connection is not available.");
            return;
        }
        const currentUserId = userId;
        const currentUserName = localStorage.getItem("userName") || (userType === "doctor" ? "Doctor" : "Patient");
        const timestamp = new Date().toISOString();
        const message = {
            action: "chat-message", message: {
                text: text,
                sender_id: currentUserId,
                sender_type: userType,
                sender_name: currentUserName,
                timestamp: timestamp
            }
        };
        setMessages(previous => [...previous, {
            id: `local-${Date.now()}-${Math.random()}`,
            message: text,
            sender_user_id: currentUserId,
            sender_username: currentUserName,
            sender_role: userType,
            timestamp: timestamp,
            is_me: true
        }]);
        try {
            socketRef.current.send(JSON.stringify(message));
            setChatMessage("");
        } catch (error) {
            console.error("Chat send error:", error);
            setError("Unable to send chat message.");
        }
    };
    // =========================================================
    // WEBSOCKET CONNECTION
    // =========================================================
    const connectSocket = (roomId) => {
        if (!roomId) {
            console.error("Cannot connect WebSocket: room ID missing.");
            setError("Telemedicine room ID is missing.");
            return;
        }
        // -----------------------------------------------------
        // JWT
        // -----------------------------------------------------
        const accessToken = getTelemedicineAccessToken();
        console.log("======================================");
        console.log("TELEMEDICINE WEBSOCKET AUTH");
        console.log("USER TYPE:", userType);
        console.log("PATIENT LOGGED IN:", patientLoggedIn);
        console.log("MAIN LOGGED IN:", mainLoggedIn);
        console.log("TOKEN EXISTS:", !!accessToken);
        console.log("======================================");
        if (!accessToken) {
            setError(patientLoggedIn ? "Patient authentication token not found." : "Authentication token not found.");
            return;
        }
        console.log("WebSocket access token found for:", userType);
        // -----------------------------------------------------
        // WEBSOCKET BASE URL
        // -----------------------------------------------------
        // -----------------------------------------------------
        // SOCKET URL
        // -----------------------------------------------------
        const socketUrl = `${wsBaseUrl}/ws/telemedicine/` + `${roomId}/` + `?token=${encodeURIComponent(accessToken)}` + `&hospital_code=${encodeURIComponent(hospitalCode || "")}`;
        console.log("Connecting Telemedicine WebSocket...");
        console.log("Room ID:", roomId);
        const ws = new WebSocket(socketUrl);
        socketRef.current = ws;
        // =====================================================
        // WS OPEN
        // =====================================================
        ws.onopen = () => {
            console.log("Telemedicine WebSocket connected");
            setSocketConnected(true);
            setError("");
            ws.send(JSON.stringify({
                action: "new-peer", peer: userType
            }));
        };
        // =====================================================
        // WS MESSAGE
        // =====================================================
        ws.onmessage = async (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log("Telemedicine WS:", data);
                // =============================================
                // CONNECTED
                // =============================================
                if (data.action === "connected") {
                    myChannelRef.current = data.channel_name;
                    console.log("My channel:", data.channel_name);
                    console.log("Authenticated user:", data.user_id);
                    console.log("Tenant:", data.tenant);
                    console.log("Hospital:", data.hospital_code);
                    return;
                }
                // =============================================
                // PEER JOINED
                // =============================================
                if (data.action === "peer-joined") {
                    console.log("Remote peer joined:", data);
                    if (data.channel_name) {
                        remoteChannelRef.current = data.channel_name;
                        setPeerConnected(true);
                        console.log("Remote peer channel:", data.channel_name);
                    }
                    return;
                }
                // =============================================
                // OFFER
                // =============================================
                if (data.action === "new-offer") {
                    await handleOffer(data.message);
                    return;
                }
                // =============================================
                // ANSWER
                // =============================================
                if (data.action === "new-answer") {
                    await handleAnswer(data.message);
                    return;
                }
                // =============================================
                // ICE
                // =============================================
                if (data.action === "new-ice-candidate") {
                    await handleIce(data.message);
                    return;
                }
                // =============================================
                // CHAT
                // =============================================
                if (data.action === "chat-message") {
                    console.log("========== CHAT WS ==========");
                    console.log("RAW DATA:", data);
                    const rawMessage = data?.message && typeof data.message === "object" ? data.message : data;
                    const text = typeof rawMessage?.text === "string" ? rawMessage.text : typeof rawMessage?.message === "string" ? rawMessage.message : "";
                    const senderId = rawMessage?.sender_id ?? data?.sender_id ?? data?.sender_user_id ?? null;
                    const senderType = rawMessage?.sender_type ?? data?.sender_type ?? data?.sender_role ?? "";
                    const senderName = rawMessage?.sender_name ?? data?.sender_name ?? data?.sender_username ?? "";
                    const timestamp = rawMessage?.timestamp ?? data?.timestamp ?? new Date().toISOString();
                    console.log("EXTRACTED:", {
                        text, senderId, senderType, senderName, timestamp
                    });
                    if (!text.trim()) {
                        console.warn("EMPTY CHAT MESSAGE");
                        return;
                    }
                    /*
                     * Ignore our own message.
                     */
                    if (String(senderId) === String(userId)) {
                        console.log("Ignoring own WS message");
                        return;
                    }
                    /*
                     * IMPORTANT:
                     * Store message as STRING, NOT OBJECT.
                     */
                    const chatItem = {
                        id: `ws-${Date.now()}-${Math.random()}`,
                        message: text,
                        sender_user_id: senderId,
                        sender_username: senderName,
                        sender_role: senderType,
                        timestamp: timestamp,
                        is_me: false
                    };
                    console.log("ADDING TO CHAT STATE:", chatItem);
                    setMessages(previous => {
                        const updated = [...previous, chatItem];
                        console.log("UPDATED MESSAGES:", updated);
                        return updated;
                    });
                    // =====================================================
// UNREAD MESSAGE COUNT
// =====================================================
                    if (!chatOpen && String(senderId) !== String(userId)) {
                        setUnreadMessages(previous => previous + 1);
                    }
                }
                // =============================================
// PEER LEFT
// =============================================
                if (data.action === "peer-left") {
                    console.log("Telemedicine peer left:", data);
                    setPeerConnected(false);
                    setCallStarted(false);
                    // Close WebRTC
                    if (pcRef.current) {
                        try {
                            pcRef.current.close();
                        } catch (error) {
                            console.error("Peer close error:", error);
                        }
                        pcRef.current = null;
                    }
                    // Clear remote video
                    if (remoteVideo.current) {
                        remoteVideo.current.srcObject = null;
                    }
                }
            } catch (error) {
                console.error("WebSocket message error:", error);
            }
        };
        // =====================================================
        // WS ERROR
        // =====================================================
        ws.onerror = (error) => {
            console.error("Telemedicine WebSocket error:", error);
            setSocketConnected(false);
            setError("Telemedicine connection error.");
        };
        // =====================================================
        // WS CLOSE
        // =====================================================
        ws.onclose = (event) => {
            console.log("Telemedicine WebSocket closed:", event.code, event.reason);
            setSocketConnected(false);
            setPeerConnected(false);
        };
    };
    useEffect(() => {
        if (chatOpen) {
            setUnreadMessages(0);
        }
    }, [chatOpen]);
    const handleChatKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            sendChatMessage();
        }
    };
    const formatChatTime = (timestamp) => {
        if (!timestamp) {
            return "";
        }
        try {
            return new Date(timestamp)
                .toLocaleTimeString([], {
                    hour: "2-digit", minute: "2-digit"
                });
        } catch (error) {
            return "";
        }
    };
    const isMyMessage = (message) => {
        const senderId = message?.message?.sender_id ?? message?.sender_user_id ?? message?.sender_id ?? message?.user_id;
        if (senderId === null || senderId === undefined || senderId === "") {
            return Boolean(message?.is_me);
        }
        return String(senderId) === String(userId);
    };
    const saveClinicalNotes = async () => {
        if (!session?.id) {
            setNotesError("Telemedicine session is not available.");
            return;
        }
        if (!clinicalNotes.trim()) {
            setNotesError("Please enter clinical notes.");
            return;
        }
        try {
            setNotesSaving(true);
            setNotesMessage("");
            setNotesError("");
            /*
             * PART 4 FRONTEND
             *
             * We will connect this to the
             * Telemedicine Consultation Notes
             * API in the next backend step.
             *
             * For now keep the data ready.
             */
            const payload = {
                session: session.id,
                appointment: appointment?.id || session?.appointment || null,
                patient: appointment?.patient?.id || null,
                notes: clinicalNotes.trim()
            };
            console.log("Clinical notes:", payload);
            /*
             * TEMPORARY
             *
             * Backend endpoint can be connected here.
             */
            setNotesMessage("Clinical notes saved.");
        } catch (error) {
            console.error("Clinical notes error:", error);
            setNotesError("Unable to save clinical notes.");
        } finally {
            setNotesSaving(false);
        }
    };
    // =========================================================
    // CREATE PEER CONNECTION
    // =========================================================
    const createPeer = () => {
        if (pcRef.current) {
            return pcRef.current;
        }
        const pc = new RTCPeerConnection({
            iceServers: [{
                urls: "stun:stun.l.google.com:19302"
            }]
        });
        // =====================================================
        // LOCAL TRACKS
        // =====================================================
        if (localStreamRef.current) {
            localStreamRef.current
                .getTracks()
                .forEach(track => {
                    pc.addTrack(track, localStreamRef.current);
                });
        }
        // =====================================================
        // REMOTE TRACK
        // =====================================================
        pc.ontrack = (event) => {
            console.log("Remote media received");
            if (remoteVideo.current) {
                remoteVideo.current.srcObject = event.streams[0];
            }
        };
        // =====================================================
        // ICE CANDIDATE
        // =====================================================
        pc.onicecandidate = (event) => {
            if (!event.candidate) {
                return;
            }
            if (!remoteChannelRef.current) {
                console.warn("Remote channel not available for ICE");
                return;
            }
            if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
                console.warn("WebSocket not available for ICE");
                return;
            }
            socketRef.current.send(JSON.stringify({
                action: "new-ice-candidate", message: {
                    candidate: event.candidate, receiver_channel_name: remoteChannelRef.current
                }
            }));
        };
        // =====================================================
        // CONNECTION STATE
        // =====================================================
        pc.onconnectionstatechange = () => {
            console.log("WebRTC connection state:", pc.connectionState);
            if (pc.connectionState === "connected") {
                setCallStarted(true);
            }
            if (pc.connectionState === "failed") {
                setError("WebRTC connection failed.");
            }
        };
        // =====================================================
        // ICE STATE
        // =====================================================
        pc.oniceconnectionstatechange = () => {
            console.log("ICE connection state:", pc.iceConnectionState);
        };
        pcRef.current = pc;
        return pc;
    };
    const toggleCamera = async () => {
        if (!localStreamRef.current) {
            try {
                await startCamera();
            } catch (error) {
                setError("Unable to access camera or microphone.");
            }
            return;
        }
        const tracks = localStreamRef.current.getVideoTracks();
        if (!tracks.length) {
            return;
        }
        const newState = !tracks[0].enabled;
        tracks.forEach(track => {
            track.enabled = newState;
        });
        setCameraStarted(newState);
    };
    const toggleMicrophone = () => {
        if (!localStreamRef.current) {
            return;
        }
        const tracks = localStreamRef.current.getAudioTracks();
        if (!tracks.length) {
            return;
        }
        const newState = !tracks[0].enabled;
        tracks.forEach(track => {
            track.enabled = newState;
        });
        setMicrophoneStarted(newState);
    };
// START CALL
// =========================================================
    const createOffer = async () => {
        try {
            if (!session) {
                alert("Telemedicine session is not available.");
                return;
            }
            if (!sessionJoined) {
                alert("You have not joined the telemedicine session.");
                return;
            }
            if (!peerConnected) {
                alert("Patient is not connected yet.");
                return;
            }
            if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
                alert("Telemedicine connection is not ready.");
                return;
            }
            // -------------------------------------------------
            // START BACKEND SESSION
            // -------------------------------------------------
            const started = await startSession();
            if (!started) {
                return;
            }
            // -------------------------------------------------
            // CREATE WEBRTC PEER
            // -------------------------------------------------
            const pc = createPeer();
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            // -------------------------------------------------
            // SEND OFFER
            // -------------------------------------------------
            socketRef.current.send(JSON.stringify({
                action: "new-offer", message: {
                    sdp: offer, receiver_channel_name: remoteChannelRef.current
                }
            }));
        } catch (error) {
            console.error("Create offer error:", error);
            setError("Unable to start the call.");
        }
    };
    // =========================================================
    // HANDLE OFFER
    // =========================================================
    const handleOffer = async (message) => {
        try {
            const pc = createPeer();
            await pc.setRemoteDescription(new RTCSessionDescription(message.sdp));
            if (pendingIceCandidatesRef.current.length) {
                console.log("Adding queued ICE candidates:", pendingIceCandidatesRef.current.length);
                for (const candidate of pendingIceCandidatesRef.current) {
                    try {
                        await pc.addIceCandidate(candidate);
                    } catch (error) {
                        console.error("Queued ICE candidate error:", error);
                    }
                }
                pendingIceCandidatesRef.current = [];
            }
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
                return;
            }
            socketRef.current.send(JSON.stringify({
                action: "new-answer", message: {
                    sdp: answer, receiver_channel_name: message.sender_channel_name
                }
            }));
            setCallStarted(true);
            console.log("Answer sent");
        } catch (error) {
            console.error("Handle offer error:", error);
        }
    };
    // =========================================================
    // HANDLE ANSWER
    // =========================================================
    const handleAnswer = async (message) => {
        try {
            if (!pcRef.current) {
                return;
            }
            await pcRef.current.setRemoteDescription(new RTCSessionDescription(message.sdp));
            console.log("Answer received");
        } catch (error) {
            console.error("Handle answer error:", error);
        }
    };
    // =========================================================
    // HANDLE ICE
    // =========================================================
    const handleIce = async (message) => {
        try {
            if (!message?.candidate) {
                return;
            }
            const candidate = new RTCIceCandidate(message.candidate);
            /*
             * Peer connection not created yet.
             * Queue the candidate.
             */
            if (!pcRef.current) {
                console.log("Queueing ICE candidate");
                pendingIceCandidatesRef.current.push(candidate);
                return;
            }
            /*
             * Remote description not available yet.
             * Queue candidate.
             */
            if (!pcRef.current.remoteDescription) {
                console.log("Queueing ICE candidate - waiting for remote description");
                pendingIceCandidatesRef.current.push(candidate);
                return;
            }
            await pcRef.current.addIceCandidate(candidate);
            console.log("ICE candidate added");
        } catch (error) {
            console.error("ICE candidate error:", error);
        }
    };
    // =========================================================
    // CLEANUP
    // =========================================================
    const cleanup = () => {
        console.log("Cleaning up telemedicine room...");
        // =====================================================
        // SESSION ID
        // =====================================================
        const sessionId = sessionIdRef.current;
        // Prevent duplicate cleanup
        sessionIdRef.current = null;
        // =====================================================
        // LEAVE BACKEND SESSION
        // ONLY WHEN COMPONENT ACTUALLY UNMOUNTS
        // =====================================================
        if (sessionId) {
            console.log("Leaving telemedicine session during component cleanup:", sessionId);
            axios
                .post(`${baseUrl}telemedicine/sessions/${sessionId}/leave/`, {})
                .catch(error => {
                    console.error("Session leave cleanup error:", error);
                });
        }
        // =====================================================
        // WEBSOCKET
        // =====================================================
        const socket = socketRef.current;
        if (socket) {
            try {
                socket.onopen = null;
                socket.onmessage = null;
                socket.onerror = null;
                socket.onclose = null;
                if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
                    socket.close();
                }
            } catch (error) {
                console.error("Socket cleanup error:", error);
            }
        }
        // =====================================================
        // PEER CONNECTION
        // =====================================================
        const pc = pcRef.current;
        if (pc) {
            try {
                pc.ontrack = null;
                pc.onicecandidate = null;
                pc.onconnectionstatechange = null;
                pc.oniceconnectionstatechange = null;
                pc.close();
            } catch (error) {
                console.error("Peer cleanup error:", error);
            }
        }
        // =====================================================
        // CAMERA + MICROPHONE
        // =====================================================
        const stream = localStreamRef.current;
        if (stream) {
            stream.getTracks().forEach(track => {
                try {
                    track.stop();
                } catch (error) {
                    console.error("Track cleanup error:", error);
                }
            });
        }
        // =====================================================
        // SCREEN SHARE
        // =====================================================
        if (screenStreamRef.current) {
            screenStreamRef.current
                .getTracks()
                .forEach(track => {
                    try {
                        track.stop();
                    } catch (error) {
                        console.error("Screen track cleanup error:", error);
                    }
                });
        }
        // =====================================================
        // VIDEO ELEMENTS
        // =====================================================
        if (localVideo.current) {
            try {
                localVideo.current.pause();
                localVideo.current.srcObject = null;
            } catch (error) {
                console.error("Local video cleanup error:", error);
            }
        }
        if (remoteVideo.current) {
            try {
                remoteVideo.current.pause();
                remoteVideo.current.srcObject = null;
            } catch (error) {
                console.error("Remote video cleanup error:", error);
            }
        }
        // =====================================================
        // RESET REFS
        // =====================================================
        socketRef.current = null;
        pcRef.current = null;
        localStreamRef.current = null;
        screenStreamRef.current = null;
        screenTrackRef.current = null;
        myChannelRef.current = null;
        remoteChannelRef.current = null;
    };
    // =========================================================
    // LOADING
    // =========================================================
    if (loading || sessionLoading) {
        return (<div className="container mt-4">

            <div className="text-center">

                <div className="spinner-border mb-3">
                </div>

                <div>
                    Initializing telemedicine room...
                </div>

            </div>

        </div>);
    }
    // =========================================================
    // ERROR
    // =========================================================
    if (error && !appointment) {
        return (<div className="container mt-4">

            <div className="alert alert-danger">

                {error}

            </div>

        </div>);
    }
    // =========================================================
    // NO APPOINTMENT
    // =========================================================
    if (!appointment) {
        return (<div className="container mt-4">

            <div className="alert alert-warning">

                Telemedicine appointment not found.

            </div>

        </div>);
    }
    // =========================================================
    // NO SESSION
    // =========================================================
    if (!session) {
        return (<div className="container mt-4">

            <div className="alert alert-danger">

                Telemedicine session could not be created.

            </div>

        </div>);
    }
    // =========================================================
// START SCREEN SHARE
// =========================================================
    const startScreenShare = async () => {
        try {
            if (!pcRef.current) {
                setError("Video connection is not active.");
                return;
            }
            if (!navigator.mediaDevices?.getDisplayMedia) {
                setError("Screen sharing is not supported by this browser.");
                return;
            }
            const screenStream = await navigator.mediaDevices.getDisplayMedia({
                video: true
            });
            const screenTrack = screenStream.getVideoTracks()[0];
            if (!screenTrack) {
                return;
            }
            screenStreamRef.current = screenStream;
            screenTrackRef.current = screenTrack;
            // -----------------------------------------------------
            // Find current camera video sender
            // -----------------------------------------------------
            const sender = pcRef.current
                .getSenders()
                .find(sender => sender.track && sender.track.kind === "video");
            if (!sender) {
                setError("Camera video sender not found.");
                return;
            }
            // -----------------------------------------------------
            // Replace camera track with screen track
            // -----------------------------------------------------
            await sender.replaceTrack(screenTrack);
            // -----------------------------------------------------
            // Show screen locally
            // -----------------------------------------------------
            if (localVideo.current) {
                localVideo.current.srcObject = screenStream;
            }
            setScreenSharing(true);
            // -----------------------------------------------------
            // Detect browser "Stop sharing"
            // -----------------------------------------------------
            screenTrack.onended = () => {
                stopScreenShare();
            };
        } catch (error) {
            console.error("Screen sharing error:", error);
            if (error?.name !== "AbortError") {
                setError("Unable to start screen sharing.");
            }
        }
    };
    // =========================================================
// STOP SCREEN SHARE
// =========================================================
    const stopScreenShare = async () => {
        try {
            if (!pcRef.current) {
                return;
            }
            const sender = pcRef.current
                .getSenders()
                .find(sender => sender.track && sender.track.kind === "video");
            // -----------------------------------------------------
            // Get camera track
            // -----------------------------------------------------
            const cameraTrack = localStreamRef.current
                ?.getVideoTracks()
                ?.find(track => track.kind === "video");
            // -----------------------------------------------------
            // Restore camera
            // -----------------------------------------------------
            if (sender && cameraTrack) {
                await sender.replaceTrack(cameraTrack);
            }
            // -----------------------------------------------------
            // Stop screen stream
            // -----------------------------------------------------
            if (screenStreamRef.current) {
                screenStreamRef.current
                    .getTracks()
                    .forEach(track => {
                        track.stop();
                    });
            }
            screenStreamRef.current = null;
            screenTrackRef.current = null;
            // -----------------------------------------------------
            // Restore local camera preview
            // -----------------------------------------------------
            if (localVideo.current) {
                localVideo.current.srcObject = localStreamRef.current;
            }
            setScreenSharing(false);
        } catch (error) {
            console.error("Stop screen share error:", error);
        }
    };
    // =========================================================
// UI
// =========================================================
    return (<div className="telemedicine-page">

        {/* =====================================================
            TOP HEADER
        ===================================================== */}

        <div className="telemedicine-header">

            <div className="telemedicine-header-left">

                <div className="telemedicine-title-icon">
                    <i className="fa-solid fa-video"></i>
                </div>

                <div>

                    <h4 className="telemedicine-title">
                        Telemedicine Consultation
                    </h4>

                    <div className="telemedicine-subtitle">

                        <span>
                            Session #{session.id}
                        </span>

                        <span className="telemedicine-header-divider">
                            |
                        </span>

                        <span>
                            Appointment #{session.appointment}
                        </span>

                    </div>

                </div>

            </div>


            {/* =================================================
                STATUS AREA
            ================================================= */}

            <div className="telemedicine-status-area">

                <div
                    className={socketConnected ? "telemedicine-status connected" : "telemedicine-status disconnected"}
                >

                    <span className="status-dot"></span>

                    {socketConnected ? "Connected" : "Disconnected"}

                </div>


                <div
                    className={peerConnected ? "telemedicine-status connected" : "telemedicine-status waiting"}
                >

                    <span className="status-dot"></span>

                    {peerConnected ? "Participant Connected" : "Waiting for Participant"}

                </div>


                <div className="telemedicine-session-status">

                    {session.status || "CONNECTING"}

                </div>

            </div>

        </div>


        {/* =====================================================
            ERROR
        ===================================================== */}

        {error && (<div className="telemedicine-error">

            <i className="fa-solid fa-triangle-exclamation"></i>

            <span>
                    {error}
                </span>

        </div>)}


        {/* =====================================================
            MAIN WORKSPACE
        ===================================================== */}

        <div className="telemedicine-workspace">


            {/* =================================================
                VIDEO SECTION
            ================================================= */}

            <div className="telemedicine-video-section">

                <div className="telemedicine-video-container">


                    {/* =========================================
                        REMOTE VIDEO
                    ========================================= */}

                    <div className="remote-video-wrapper">

                        <video
                            ref={remoteVideo}
                            autoPlay
                            playsInline
                            className="remote-video"
                        />


                        {/* =====================================
                            WAITING OVERLAY
                        ===================================== */}

                        {!callStarted && (<div className="remote-video-overlay">

                            <div className="waiting-avatar">

                                <i className="fa-solid fa-user"></i>

                            </div>

                            <div className="waiting-title">

                                {peerConnected ? "Participant Connected" : "Waiting for Participant"}

                            </div>

                            <div className="waiting-subtitle">

                                {peerConnected ? "Ready to start consultation" : "The other participant has not joined yet"}

                            </div>

                        </div>)}


                        {/* =====================================
                            REMOTE LABEL
                        ===================================== */}

                        <div className="remote-participant-label">

                            <span className="participant-live-dot"></span>

                            {userType === "doctor" ? "Patient" : "Doctor"}

                        </div>


                        {/* =====================================
                            ROOM INFO
                        ===================================== */}

                        <div className="remote-room-info">

                            Room: {session.room_id}

                        </div>

                    </div>


                    {/* =========================================
                        LOCAL VIDEO — FLOATING
                    ========================================= */}

                    <div className="local-video-wrapper">
                        {!cameraStarted && (<div className="local-video-off">

                            <i className="fa-solid fa-video-slash"></i>

                        </div>)}

                        <video
                            ref={localVideo}
                            autoPlay
                            muted
                            playsInline
                            className="local-video"
                        />


                        <div className="local-video-label">

                            You ({userType})

                        </div>

                    </div>


                    {/* =========================================
                        CALL ACTIVE INDICATOR
                    ========================================= */}

                    {callStarted && (<div className="call-active-indicator">

                        <span className="call-active-dot"></span>

                        Consultation Active

                    </div>)}

                </div>


                {/* =================================================
                    VIDEO CONTROL BAR
                ================================================= */}

                <div className="telemedicine-controls">


                    {/* =========================================
                        CAMERA / MIC STATUS
                    ========================================= */}

                    <div className="telemedicine-control-group">


                        <div
                            className={cameraStarted ? "device-status active" : "device-status inactive"}
                        >

                            <i
                                className={cameraStarted ? "fa-solid fa-video" : "fa-solid fa-video-slash"}
                            ></i>

                            <span>
                                {cameraStarted ? "Camera" : "Camera Off"}
                            </span>

                        </div>


                        <div
                            className={cameraStarted ? "device-status active" : "device-status inactive"}
                        >

                            <i className="fa-solid fa-microphone"></i>

                            <span>
                                {cameraStarted ? "Microphone" : "Mic Off"}
                            </span>

                        </div>

                    </div>

                    <div
                        className={microphoneStarted ? "device-status active" : "device-status inactive"}
                    >

                        <i
                            className={microphoneStarted ? "fa-solid fa-microphone" : "fa-solid fa-microphone-slash"}
                        ></i>

                        <span>
        {microphoneStarted ? "Microphone" : "Mic Off"}
    </span>

                    </div>
                    {/* =========================================
                        CENTER CONTROLS
                    ========================================= */}

                    <div className="telemedicine-main-controls">


                        {/* CAMERA BUTTON */}

                        <button
                            type="button"
                            className={cameraStarted ? "telemedicine-round-btn" : "telemedicine-round-btn danger"}
                            onClick={toggleCamera}
                            title="Camera"
                        >
                            <i
                                className={cameraStarted ? "fa-solid fa-video" : "fa-solid fa-video-slash"}
                            ></i>
                        </button>


                        {/* MICROPHONE BUTTON */}

                        <button
                            type="button"
                            className={microphoneStarted ? "telemedicine-round-btn" : "telemedicine-round-btn danger"}
                            onClick={toggleMicrophone}
                            title="Microphone"
                        >
                            <i
                                className={microphoneStarted ? "fa-solid fa-microphone" : "fa-solid fa-microphone-slash"}
                            ></i>
                        </button>

                        {/* SCREEN SHARE — placeholder for Part 3 */}

                        <button
                            type="button"
                            className={screenSharing ? "telemedicine-round-btn active" : "telemedicine-round-btn"}
                            onClick={screenSharing ? stopScreenShare : startScreenShare}
                            disabled={!callStarted}
                            title={screenSharing ? "Stop screen sharing" : "Share screen"}
                        >

                            <i
                                className={screenSharing ? "fa-solid fa-display-slash" : "fa-solid fa-display"}
                            ></i>

                        </button>

                        {/* CHAT — placeholder for Part 4 */}

                        <button
                            type="button"
                            className={chatOpen ? "telemedicine-round-btn active" : "telemedicine-round-btn"}
                            onClick={() => setChatOpen(previous => !previous)}
                            title="Chat"
                        >

                            <i className="fa-solid fa-comment"></i>

                            {unreadMessages > 0 && (<span className="chat-count">
            {unreadMessages}
        </span>)}

                        </button>

                    </div>
                    {/* =====================================================
    LIVE CHAT
===================================================== */}

                    {chatOpen && (<div className="telemedicine-chat-panel">

                        {/* =================================================
            CHAT HEADER
        ================================================= */}

                        <div className="telemedicine-chat-header">

                            <div className="telemedicine-chat-title">

                                <div className="telemedicine-chat-icon">

                                    <i className="fa-solid fa-comments"></i>

                                </div>

                                <div>

                                    <div className="telemedicine-chat-name">

                                        Consultation Chat

                                    </div>

                                    <div className="telemedicine-chat-status">

                                        <span className="chat-online-dot"></span>

                                        {peerConnected ? "Participant online" : "Waiting for participant"}

                                    </div>

                                </div>

                            </div>


                            <button
                                type="button"
                                className="telemedicine-chat-close"
                                onClick={() => setChatOpen(false)}
                            >

                                <i className="fa-solid fa-xmark"></i>

                            </button>

                        </div>


                        {/* =================================================
            MESSAGES
        ================================================= */}

                        <div className="telemedicine-chat-messages">

                            {messages.length === 0 ? (<div className="telemedicine-chat-empty">

                                <div className="telemedicine-chat-empty-icon">
                                    <i className="fa-regular fa-comments"></i>
                                </div>

                                <div className="telemedicine-chat-empty-title">
                                    No messages yet
                                </div>

                                <div className="telemedicine-chat-empty-text">
                                    Send a message to the participant
                                    during the consultation.
                                </div>

                            </div>) : (messages.map((message, index) => {
                                const text = typeof message?.message === "string" ? message.message : "";
                                const mine = message?.is_me === true;
                                const senderName = message?.sender_username || (mine ? "You" : "Participant");
                                const timestamp = message?.timestamp || "";
                                if (!text.trim()) {
                                    return null;
                                }
                                return (<div
                                    key={message?.id || `message-${index}`}
                                    className={mine ? "telemedicine-chat-row mine" : "telemedicine-chat-row"}
                                >

                                    <div
                                        className={mine ? "telemedicine-chat-bubble mine" : "telemedicine-chat-bubble"}
                                    >

                                        {!mine && (<div className="telemedicine-chat-sender">
                                            {senderName}
                                        </div>)}

                                        <div className="telemedicine-chat-text">
                                            {text}
                                        </div>

                                        <div className="telemedicine-chat-time">
                                            {formatChatTime(timestamp)}
                                        </div>

                                    </div>

                                </div>);
                            }))}
                        </div>


                        {/* =================================================
            MESSAGE INPUT
        ================================================= */}

                        <div className="telemedicine-chat-input-area">

            <textarea
                value={chatMessage}
                onChange={event => setChatMessage(event.target.value)}
                onKeyDown={handleChatKeyDown}
                placeholder="Type a message..."
                rows={1}
                disabled={!socketConnected}
            />

                            <button
                                type="button"
                                className="telemedicine-chat-send"
                                onClick={sendChatMessage}
                                disabled={!chatMessage.trim() || !socketConnected}
                            >

                                <i className="fa-solid fa-paper-plane"></i>

                            </button>

                        </div>

                    </div>)}

                    {/* =========================================
                        DOCTOR START CALL
                    ========================================= */}

                    <div className="telemedicine-call-actions">

                        {userType === "doctor" && session.status !== "ENDED" && !sessionStarted && (<button
                            type="button"
                            className="telemedicine-start-btn"
                            onClick={createOffer}
                            disabled={!sessionJoined || !peerConnected}
                        >

                            <i className="fa-solid fa-phone me-2"></i>

                            {!sessionJoined ? "Joining..." : !peerConnected ? "Waiting..." : "Start Consultation"}

                        </button>)}


                        {/* =====================================
                            ACTIVE CALL
                        ===================================== */}

                        {sessionStarted && (<button
                            type="button"
                            className="telemedicine-end-btn"
                            onClick={endSession}
                            disabled={endingCall}
                        >

                            <i className="fa-solid fa-phone-slash me-2"></i>

                            {endingCall ? "Ending..." : "End Consultation"}

                        </button>)}

                    </div>

                </div>

            </div>


            {/* =================================================
                RIGHT SIDEBAR
            ================================================= */}

            <div className="telemedicine-sidebar">


                {/* =================================================
                    PATIENT / PARTICIPANT
                ================================================= */}

                <div className="telemedicine-panel">

                    <div className="telemedicine-panel-header">

                        <div>

                            <i className="fa-solid fa-user-injured me-2"></i>

                            Patient

                        </div>

                        <span className="panel-online">

                            {peerConnected ? "Online" : "Offline"}

                        </span>

                    </div>


                    <div className="telemedicine-patient-card">

                        <div className="patient-avatar">

                            <i className="fa-solid fa-user"></i>

                        </div>

                        <div className="patient-details">

                            <div className="patient-name">

                                {appointment?.patient_name || "Patient"}
                            </div>

                            <div className="patient-meta">

                                Telemedicine Patient

                            </div>

                        </div>

                    </div>


                    <div className="patient-information">

                        <div className="patient-info-item">

                            <span>
                                Appointment
                            </span>

                            <strong>
                                #{session.appointment}
                            </strong>

                        </div>


                        <div className="patient-info-item">

                            <span>
                                Session
                            </span>

                            <strong>
                                #{session.id}
                            </strong>

                        </div>


                        <div className="patient-info-item">

                            <span>
                                Room
                            </span>

                            <strong className="room-text">

                                {session.room_id}

                            </strong>

                        </div>

                    </div>

                </div>
                {canViewClinicalSection && (<>
                    {/* =========================================================
        VITALS
========================================================= */}

                    <div className="card telemedicine-card mt-3">

                        <div className="card-header d-flex justify-content-between align-items-center">

                            <div>

                                <i className="fa-solid fa-heart-pulse me-2"></i>

                                Patient Vitals

                            </div>

                            {vitalsLoading && (<span className="small text-muted">

                Loading...

            </span>)}

                        </div>

                        <div className="card-body">

                            <div className="row g-3">

                                {/* =========================================
                    BLOOD PRESSURE
            ========================================= */}
                                <div className="col-md-6 col-lg-4">
                                    <label className="form-label">Blood Pressure</label>

                                    <div className="mb-2">
                                        <div className="input-group">
            <span className="input-group-text" style={{minWidth: "80px"}}>
                Systolic
            </span>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="systolic"
                                                value={vitals.systolic}
                                                onChange={handleVitalsChange}
                                                placeholder="120"
                                            />
                                            <span className="input-group-text">mmHg</span>
                                        </div>
                                    </div>

                                    <div className="input-group">
        <span className="input-group-text" style={{minWidth: "80px"}}>
            Diastolic
        </span>
                                        <input
                                            type="number"
                                            className="form-control"
                                            name="diastolic"
                                            value={vitals.diastolic}
                                            onChange={handleVitalsChange}
                                            placeholder="80"
                                        />
                                        <span className="input-group-text">mmHg</span>
                                    </div>
                                </div>

                                {/* =========================================
                    PULSE
            ========================================= */}

                                <div className="col-md-6 col-lg-4">

                                    <label className="form-label">

                                        Pulse

                                    </label>

                                    <div className="input-group">

                                        <input
                                            type="number"
                                            className="form-control"
                                            name="pulse"
                                            value={vitals.pulse}
                                            onChange={handleVitalsChange}
                                            placeholder="72"
                                            min="0"
                                        />

                                        <span className="input-group-text">

                        bpm

                    </span>

                                    </div>

                                </div>


                                {/* =========================================
                    SPO2
            ========================================= */}

                                <div className="col-md-6 col-lg-4">

                                    <label className="form-label">

                                        SpO₂

                                    </label>

                                    <div className="input-group">

                                        <input
                                            type="number"
                                            className="form-control"
                                            name="spo2"
                                            value={vitals.spo2}
                                            onChange={handleVitalsChange}
                                            placeholder="98"
                                            min="0"
                                            max="100"
                                        />

                                        <span className="input-group-text">

                        %

                    </span>

                                    </div>

                                </div>


                                {/* =========================================
                    TEMPERATURE
            ========================================= */}

                                <div className="col-md-6 col-lg-4">

                                    <label className="form-label">

                                        Temperature

                                    </label>

                                    <div className="input-group">

                                        <input
                                            type="number"
                                            step="0.1"
                                            className="form-control"
                                            name="temperature"
                                            value={vitals.temperature}
                                            onChange={handleVitalsChange}
                                            placeholder="98.6"
                                        />

                                        <span className="input-group-text">

                        °F

                    </span>

                                    </div>

                                </div>


                                {/* =========================================
                    RESPIRATORY RATE
            ========================================= */}

                                <div className="col-md-6 col-lg-4">

                                    <label className="form-label">

                                        Respiratory Rate

                                    </label>

                                    <div className="input-group">

                                        <input
                                            type="number"
                                            className="form-control"
                                            name="respiratory_rate"
                                            value={vitals.respiratory_rate}
                                            onChange={handleVitalsChange}
                                            placeholder="16"
                                            min="0"
                                        />

                                        <span className="input-group-text">

                        /min

                    </span>

                                    </div>

                                </div>


                                {/* =========================================
                    BLOOD GLUCOSE
            ========================================= */}

                                <div className="col-md-6 col-lg-4">

                                    <label className="form-label">

                                        Blood Glucose

                                    </label>

                                    <div className="input-group">

                                        <input
                                            type="number"
                                            className="form-control"
                                            name="blood_glucose"
                                            value={vitals.blood_glucose}
                                            onChange={handleVitalsChange}
                                            placeholder="100"
                                            min="0"
                                        />

                                        <span className="input-group-text">

                        mg/dL

                    </span>

                                    </div>

                                </div>


                                {/* =========================================
                    SAVE
            ========================================= */}

                                <div className="col-md-6 col-lg-4 d-flex align-items-end">

                                    <button
                                        type="button"
                                        className="btn btn-primary w-100"
                                        onClick={saveVitals}
                                        disabled={vitalsSaving}
                                    >

                                        {vitalsSaving ? (<>
                            <span
                                className="spinner-border spinner-border-sm me-2"
                            />

                                            Saving...

                                        </>) : (<>

                                            Save Vitals

                                        </>)}

                                    </button>

                                </div>

                            </div>


                            {/* =========================================
                SUCCESS
        ========================================= */}

                            {vitalsMessage && (<div className="alert alert-success mt-3 mb-0">

                                <i className="fa-solid fa-circle-check me-2"></i>

                                {vitalsMessage}

                            </div>)}


                            {/* =========================================
                ERROR
        ========================================= */}

                            {vitalsError && (<div className="alert alert-danger mt-3 mb-0">

                                <i className="fa-solid fa-triangle-exclamation me-2"></i>

                                {vitalsError}

                            </div>)}

                        </div>

                    </div>
                    {/* =========================================================
    CLINICAL NOTES
========================================================= */}

                    <div className="telemedicine-panel clinical-notes-panel">

                        <div className="telemedicine-panel-header">

                            <div>

                                <i className="fa-solid fa-notes-medical me-2"></i>

                                Consultation Notes

                            </div>

                            <span className="notes-badge">

            Clinical

        </span>

                        </div>


                        <div className="clinical-notes-body">

        <textarea
            className="clinical-notes-textarea"
            value={clinicalNotes}
            onChange={event => {
                setClinicalNotes(event.target.value);
                setNotesMessage("");
                setNotesError("");
            }}
            placeholder={"Enter consultation notes...\n\n" + "• Chief complaint\n" + "• History of present illness\n" + "• Examination findings\n" + "• Assessment\n" + "• Plan"}
        />


                            <div className="clinical-notes-footer">

            <span className="clinical-notes-hint">

                <i className="fa-solid fa-lock me-1"></i>

                Clinical record

            </span>


                                <button
                                    type="button"
                                    className="clinical-notes-save-btn"
                                    onClick={saveClinicalNotes}
                                    disabled={notesSaving}
                                >

                                    {notesSaving ? (<>
                        <span
                            className="spinner-border spinner-border-sm me-2"
                        />

                                        Saving...

                                    </>) : (<>
                                        <i className="fa-solid fa-floppy-disk me-2"></i>

                                        Save Notes

                                    </>)}

                                </button>

                            </div>


                            {notesMessage && (<div className="clinical-notes-success">

                                <i className="fa-solid fa-circle-check me-2"></i>

                                {notesMessage}

                            </div>)}


                            {notesError && (<div className="clinical-notes-error">

                                <i className="fa-solid fa-triangle-exclamation me-2"></i>

                                {notesError}

                            </div>)}

                        </div>

                    </div>
                    {/* =================================================
                    AI COPILOT PLACEHOLDER
                ================================================= */}

                    {/* =================================================
    AI CLINICAL COPILOT
================================================= */}

                    <div className="telemedicine-panel ai-panel">

                        {/* =================================================
        HEADER
    ================================================= */}

                        <div className="telemedicine-panel-header">

                            <div>

                                <i className="fa-solid fa-wand-magic-sparkles me-2"></i>

                                AI Clinical Copilot

                            </div>

                            <span className="ai-badge">

            AI

        </span>

                        </div>


                        {/* =================================================
        AI ACTION
    ================================================= */}

                        <div className="ai-copilot-body">

                            <button
                                type="button"
                                className="ai-analyze-btn"
                                onClick={runTelemedicineAI}
                                disabled={aiLoading || session.status === "ENDED"}
                            >
                                {aiLoading ? (<>
                                    <span className="spinner-border spinner-border-sm me-2"/>

                                    Analyzing Clinical Data...
                                </>) : (<>
            <span
                className="me-2"
                style={{
                    display: "inline-flex", alignItems: "center"
                }}
            >
                <SparklesIcon size={18}/>
            </span>

                                    Analyze Consultation
                                </>)}
                            </button>

                            {/* =================================================
            STATUS
        ================================================= */}

                            {aiMessage && (<div className="ai-success-message">

                                <i className="fa-solid fa-circle-check me-2"></i>

                                {aiMessage}

                            </div>)}


                            {aiError && (<div className="ai-error-message">

                                <i className="fa-solid fa-triangle-exclamation me-2"></i>

                                {aiError}

                            </div>)}


                            {/* =================================================
            AI NAVIGATION
        ================================================= */}

                            <div className="ai-tabs">

                                <button
                                    type="button"
                                    className={activeAISection === "analysis" ? "ai-tab active" : "ai-tab"}
                                    onClick={() => setActiveAISection("analysis")}
                                >

                                    <i className="fa-solid fa-stethoscope"></i>

                                    Analysis

                                </button>


                                <button
                                    type="button"
                                    className={activeAISection === "summary" ? "ai-tab active" : "ai-tab"}
                                    onClick={() => setActiveAISection("summary")}
                                >

                                    <i className="fa-solid fa-file-medical"></i>

                                    Summary

                                </button>


                                <button
                                    type="button"
                                    className={activeAISection === "notes" ? "ai-tab active" : "ai-tab"}
                                    onClick={() => setActiveAISection("notes")}
                                >

                                    <i className="fa-solid fa-notes-medical"></i>

                                    Notes

                                </button>

                            </div>


                            {/* =================================================
            ANALYSIS
        ================================================= */}

                            {activeAISection === "analysis" && (<div className="ai-result-section">

                                {!aiAnalysis ? (<div className="ai-empty-result">

                                    <i className="fa-solid fa-brain"></i>

                                    <div>

                                        No AI analysis available yet.

                                    </div>

                                    <small>

                                        Run clinical analysis after the
                                        consultation data is available.

                                    </small>

                                </div>) : (<>

                                    {/* ---------------------------------
                            OVERALL
                        --------------------------------- */}

                                    {aiAnalysis.overall_assessment && (<div className="ai-result-card">

                                        <div className="ai-result-title">

                                            <i className="fa-solid fa-clipboard-check me-2"></i>

                                            Clinical Assessment

                                        </div>

                                        <div className="ai-result-text">

                                            {aiAnalysis.overall_assessment}

                                        </div>

                                    </div>)}


                                    {/* ---------------------------------
                            RED FLAGS
                        --------------------------------- */}

                                    {Array.isArray(aiAnalysis.red_flags) && aiAnalysis.red_flags.length > 0 && (
                                        <div className="ai-result-card ai-red-flag-card">

                                            <div className="ai-result-title">

                                                <i className="fa-solid fa-triangle-exclamation me-2"></i>

                                                Red Flags

                                            </div>

                                            {aiAnalysis.red_flags.map((flag, index) => (<div
                                                className="ai-list-item"
                                                key={index}
                                            >

                                            <span>
                                                {typeof flag === "object" ? flag.message || flag.description || JSON.stringify(flag) : flag}
                                            </span>

                                            </div>))}

                                        </div>)}


                                    {/* ---------------------------------
                            RECOMMENDATIONS
                        --------------------------------- */}

                                    {Array.isArray(aiAnalysis.recommendations) && aiAnalysis.recommendations.length > 0 && (
                                        <div className="ai-result-card">

                                            <div className="ai-result-title">

                                                <i className="fa-solid fa-lightbulb me-2"></i>

                                                Clinical Suggestions

                                            </div>

                                            {aiAnalysis.recommendations.map((item, index) => (<div
                                                className="ai-list-item"
                                                key={index}
                                            >

                                            <span>

                                                {typeof item === "object" ? item.message || item.description || JSON.stringify(item) : item}

                                            </span>

                                            </div>))}

                                        </div>)}

                                </>)}

                            </div>)}


                            {/* =================================================
            SUMMARY
        ================================================= */}

                            {activeAISection === "summary" && (<div className="ai-result-section">

                                {!aiSummary ? (<div className="ai-empty-result">

                                    <i className="fa-solid fa-file-medical"></i>

                                    <div>

                                        No consultation summary available.

                                    </div>

                                </div>) : (<div className="ai-result-card">

                                    <div className="ai-result-title">

                                        <i className="fa-solid fa-file-medical me-2"></i>

                                        Consultation Summary

                                    </div>

                                    <div className="ai-summary-text">

                                        {aiSummary.summary || aiSummary.content || aiSummary.description || "Summary generated successfully."}

                                    </div>

                                </div>)}

                            </div>)}


                            {/* =================================================
            CLINICAL NOTES
        ================================================= */}

                            {activeAISection === "notes" && (<div className="ai-result-section">

                                {aiClinicalNotes.length === 0 ? (<div className="ai-empty-result">

                                    <i className="fa-solid fa-notes-medical"></i>

                                    <div>

                                        No AI clinical notes available.

                                    </div>

                                </div>) : (aiClinicalNotes.map((note, index) => (<div
                                    className="ai-result-card"
                                    key={note.id || index}
                                >

                                    <div className="ai-result-title">

                                        <i className="fa-solid fa-notes-medical me-2"></i>

                                        Clinical Note

                                    </div>

                                    <div className="ai-summary-text">

                                        {note.note || note.content || note.clinical_note || note.description || JSON.stringify(note)}

                                    </div>

                                    {note.status && (<div className="ai-note-status">

                                        Status: {note.status}

                                    </div>)}

                                </div>)))}

                            </div>)}

                        </div>

                    </div>
                </>)}

                {/* =================================================
                    CONNECTION INFORMATION
                ================================================= */}

                <div className="telemedicine-connection-info">

                    <div>

                        <span>
                            WebSocket
                        </span>

                        <strong>
                            {socketConnected ? "Connected" : "Disconnected"}
                        </strong>

                    </div>


                    <div>

                        <span>
                            WebRTC
                        </span>

                        <strong>
                            {callStarted ? "Connected" : "Waiting"}
                        </strong>

                    </div>


                    <div>

                        <span>
                            Camera
                        </span>

                        <strong>
                            {cameraStarted ? "Active" : "Inactive"}
                        </strong>

                    </div>

                </div>

            </div>

        </div>

    </div>);
}
export default TelemedicineRoom;
